import { type RefObject, useEffect } from 'react';

/**
 * Moves a soft highlight across a glass surface to follow the pointer, the
 * way a light source catches a curved pane as you move past it.
 *
 * Writes CSS custom properties straight to the element, never to React
 * state: pointermove fires at display refresh rate, and a re-render per
 * event is exactly what the store contract forbids. A custom property on
 * the element itself invalidates only that element's own style.
 *
 * Coarse pointers are skipped. A touch has no hover, so the highlight would
 * only ever flash at the tap point and stick there.
 */
export const usePointerGlare = (ref: RefObject<HTMLElement | null>, enabled: boolean): void => {
  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element || !window.matchMedia('(hover: hover)').matches) return;

    const handleMove = (event: PointerEvent): void => {
      const rect = element.getBoundingClientRect();
      element.style.setProperty('--glare-x', `${String(event.clientX - rect.left)}px`);
      element.style.setProperty('--glare-y', `${String(event.clientY - rect.top)}px`);
    };
    const handleEnter = (): void => {
      element.style.setProperty('--glare-opacity', '1');
    };
    const handleLeave = (): void => {
      element.style.setProperty('--glare-opacity', '0');
    };

    element.addEventListener('pointermove', handleMove);
    element.addEventListener('pointerenter', handleEnter);
    element.addEventListener('pointerleave', handleLeave);

    return () => {
      element.removeEventListener('pointermove', handleMove);
      element.removeEventListener('pointerenter', handleEnter);
      element.removeEventListener('pointerleave', handleLeave);
    };
  }, [ref, enabled]);
};
